import { createApi } from "@reduxjs/toolkit/query/react";

import {
    ICreatePartnerBody,
    ICreatePartnerResponse,
    IDeletePartnerBody,
    IDeletePartnerResponse,
    IGetPartnerByIdResponse,
    IGetPartnerProjectsResponse,
    IGetPartnersResponse,
    ISetPartnerProjectsBody,
    ISetPartnerProjectsResponse,
    IUpdatePartnerBody,
    IUpdatePartnerResponse,
} from "../../types/partners";
import { baseQueryWithIntercept } from "../connection/api";

const ENDPOINT_PREFIX = "partners";

export const partnersApi = createApi({
    reducerPath: "partnersApi",
    baseQuery: baseQueryWithIntercept,
    tagTypes: ["Partners", "Partner", "PartnerProjects"],
    endpoints: build => ({
        getPartners: build.query<IGetPartnersResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/`,
            }),
            providesTags: (result, _error, _arg) =>
                result?.data.partners ? [...result.data.partners.map(({ id }) => ({ type: "Partners" as const, id })), "Partners"] : ["Partners"],
        }),
        createPartner: build.mutation<ICreatePartnerResponse, ICreatePartnerBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Partners"],
        }),
        getPartnerById: build.query<IGetPartnerByIdResponse, { partnerId: string }>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/id/${data.partnerId}`,
                method: "GET",
            }),
            providesTags: (result, _error, arg) => [{ type: "Partner", id: arg.partnerId }],
        }),
        getPartnerProjects: build.query<IGetPartnerProjectsResponse, { partnerId: string }>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.partnerId}/projects`,
                method: "GET",
            }),
            providesTags: (_result, _error, arg) => [{ type: "PartnerProjects", id: arg.partnerId }],
        }),
        updatePartner: build.mutation<IUpdatePartnerResponse, IUpdatePartnerBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.partnerId}`,
                method: "PUT",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Partner", id: arg.partnerId }, "Partners"],
        }),
        setPartnerProjects: build.mutation<ISetPartnerProjectsResponse, ISetPartnerProjectsBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.partnerId}/projects`,
                method: "PATCH",
                body: {
                    projectIds: data.projectIds,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "PartnerProjects", id: arg.partnerId }, { type: "Partner", id: arg.partnerId }, "Partners"],
        }),
        deletePartner: build.mutation<IDeletePartnerResponse, IDeletePartnerBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.partnerId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Partner", id: arg.partnerId }, "Partners"],
        }),
    }),
});

export const {
    useGetPartnersQuery,
    useCreatePartnerMutation,
    useGetPartnerByIdQuery,
    useGetPartnerProjectsQuery,
    useSetPartnerProjectsMutation,
    useUpdatePartnerMutation,
    useDeletePartnerMutation,
} = partnersApi;
